/**
 * The deposit dialog's rules, kept free of Vue so they can be read and tested on their own.
 *
 * Client and matter arrive in two shapes: the picker's lean option record ({ id, label }) and,
 * once fetched, the full record with `billing` and `accounting`. Every rule below treats a
 * record without `billing` as "not known yet" rather than as "no trust account".
 */

import type {
  LooseRecord,
  TrustAccountSetting,
} from '@/models/trustDepositTypes';

type Rec = LooseRecord | null | undefined;

/** True when recording this deposit will create the trust account(s) in QuickBooks. */
export function isFirstDeposit(client: Rec, matter: Rec): boolean {
  if (!client) return false;
  if (client.billing?.billClient) return !client.billing.trust;
  return !!matter && !matter.billing?.trust;
}

/** Offer a trust account choice only while the record has none attached and there is a choice. */
export function shouldOfferTrustAccount(
  trusts: TrustAccountSetting[],
  client: Rec,
  matter: Rec,
): boolean {
  if (!(trusts.length > 1)) return false;
  // Waiting for .billing, not just the client, keeps the field from flashing in on the lean
  // option record before the full one reveals an already-attached account.
  if (!client?.billing) return false;
  if (client.billing.billClient) return !client.billing.trust;
  return !!matter && !matter.billing?.trust;
}

/** QuickBooks truncates account names longer than 100 characters. */
export function trustAccountNameTooLong(client: Rec, matter: Rec): boolean {
  const label = client?.billing?.billClient ? client?.label : matter?.label;
  return (label?.length ?? 0) > 100;
}

export function buildDepositMemo(source?: string, use?: string): string {
  return `${source ? `From: ${source}\n` : ''}${use ? `Use: ${use}\n` : ''}`;
}

/**
 * The account the record is attached to, else `preferredId` (the account the dialog was opened
 * from), else the firm default. An attachment is permanent once made, so it wins over both; one
 * that was since removed in settings falls back.
 */
export function resolveTrustAccount(
  trusts: TrustAccountSetting[],
  client: Rec,
  matter: Rec,
  preferredId?: string,
): TrustAccountSetting | undefined {
  const attachedId = client?.billing?.billClient
    ? client.billing.trust?.id
    : matter?.billing?.trust?.id;
  return (
    (attachedId && trusts.find((t) => t.id === attachedId)) ||
    (preferredId && trusts.find((t) => t.id === preferredId)) ||
    trusts.find((t) => t.defaultAccount)
  );
}

/**
 * The message to show for a failed request, from the pre-migration formatApiError. The server's
 * body comes from an axios error's `response.data`, or leanLawApi's `{ status, body }`: its
 * `message`, else its `detail`, else a plain-string body, as before the migration — except an HTML error page. A
 * non-axios error without a body falls back to its own `message`.
 */
export function formatDepositError(err: unknown): string {
  const e = err as LooseRecord | null | undefined;
  const body = e?.response?.data ?? e?.body;
  let message: string | undefined;
  let detail: string | undefined;
  if (typeof err === 'string') {
    message = err;
  } else if (body) {
    if (typeof body === 'string') {
      // A gateway's HTML error page is not a message anyone can read in a toast.
      if (!/^\s*</.test(body)) message = body;
    } else {
      message = body.message;
      detail = body.detail;
    }
  }
  // axios's own message ("Request failed with status code 500", "Network Error") says nothing useful.
  const ownMessage = e?.isAxiosError ? undefined : e?.message;
  message =
    message ||
    detail ||
    ownMessage ||
    'An unexpected error occurred. Please try again.';

  const fullText = [message, detail].filter(Boolean).join(' ');
  if (/Account assigned to this transaction has been deleted/i.test(fullText)) {
    const accountName = fullText
      .match(/you must restore\s+(.+?)(?:\.\.|$)/i)?.[1]
      ?.trim();
    if (accountName) {
      return `The QuickBooks account "${accountName}" has been deleted or disabled. Please restore it in QuickBooks Online before recording this deposit.`;
    }
    return 'A QuickBooks account linked to this transaction has been deleted or disabled in QuickBooks Online. Please restore it in QuickBooks before recording this deposit.';
  }
  return message as string;
}
